import { captureMessage } from '@sentry/browser';
import React from 'react';
import { Batch } from '../../../../common/src/domain/batch/batchTableSchema';
import { Company } from '../../../../common/src/domain/company/companyTableSchema';
import { BatchStates } from '../../../../common/src/enums/BatchStates';
import { IWithReactRouterProps } from '../../mixins/router/withReactRouter';
import { ITranslateProps, translate } from '../../mixins/translate';
import { fetchDelete, fetchGet, fetchPost } from '../../store/fetch';
import { Roles } from '../../utils/enums/Roles';
import { IconRemoveButton } from '../common/iconButtons/IconRemoveButton';
import {
    Alert,
    Content,
    EditPanelButtons,
    LeftTray,
    LeftTrayHeader,
    LeftTrayItem,
    LeftTrayList,
    PanelBody,
    PanelButtonLink,
    PanelHeading,
    PanelLink,
    PanelRouterLink,
    PanelWithoutForm,
    Table,
    TableBody,
    TableData,
    TableHead,
    TableHeader,
    TableHeadRow,
    TableRow,
    Topbar,
    TopbarCrumbActive,
    TopbarLeft,
    TopbarRight,
    Tray,
} from '../elements';
import { Spinner } from '../spinners';
import { BatchStatusLabel } from '../utils/BatchStatusLabel';
import { Component } from '../utils/Component';
import { NullableFormattedDate } from '../utils/NullableFormattedDate';
import RoleDependent from '../utils/RoleDependent';
import { BatchDetailsAddObject } from './BatchDetailsAddObject';
import { BatchDetailsDocuments } from './BatchDetailsDocuments';

interface IBatchObject {
    objectId: string;
    object: IBatchDetailsObject;
    batches: {
        id: string;
        name: string;
        plannedStartDate: string;
        plannedEndDate: string;
        status: string;
    }[];
}

export interface IBatchDetailsObject {
    id: string;
    name: string;
    location?: undefined | string;
    usage?: undefined | string;
    inspectionStandardType?: undefined | string;
    objectTypeName?: undefined | string;
    status?: undefined | string;
    batches: any[];
}

interface IBatchDetailsProps extends ITranslateProps, IWithReactRouterProps {
    match: { params: { id?: undefined | string } };
}

type BatchExecutorCompanyView = { company: Pick<Company, 'id' | 'name'> } & { batch: Pick<Batch, 'id' | 'name'> };

interface IBatchDetailsState {
    isLoading: boolean;
    name?: undefined | string;
    startDate?: undefined | Date;
    endDate?: undefined | Date;
    plannedStartDate?: undefined | Date;
    plannedEndDate?: undefined | Date;
    status?: undefined | string;
    inspectionStandardType?: undefined | string;
    batchObjects: IBatchObject[];
    executorsWithSameObject: BatchExecutorCompanyView[];
    submitting: boolean;
    showObjectForm: boolean;
    showObjectListForm: boolean;
    importObjectList: string;
    importObjectListResult?: undefined | { succeededCodes: string[]; notFoundCodes: string[]; nonUniqueCodes: string[] };
    executorCompanies: { company: { id: string; name: string } }[];
    showRelatedBatches: boolean;
    batchId: string;
}

class BatchDetailsComponent extends Component<IBatchDetailsProps, IBatchDetailsState> {
    constructor(props: IBatchDetailsProps) {
        super(props);

        if (!props.match.params.id) {
            throw new Error('Batch id is missing: props.match.params.id');
        }

        this.state = {
            isLoading: false,
            showObjectForm: false,
            showObjectListForm: false,
            submitting: false,
            batchObjects: [],
            importObjectList: '',
            executorCompanies: [],
            executorsWithSameObject: [],
            showRelatedBatches: false,
            batchId: props.match.params.id,
        };
    }

    public override async componentDidMount() {
        await this.load();
        await this.loadExecutorsWithSameObject();
    }

    public override async componentDidUpdate(prevProps: IBatchDetailsProps) {
        if (prevProps.match.params.id !== this.props.match.params.id) {
            const batchId = this.props.match.params.id;
            if (!batchId) {
                throw new Error('Batch id is missing: props.match.params.id');
            }
            await this.setStatePromised((i) => ({ ...i, batchId }));
            await this.load();
            await this.loadExecutorsWithSameObject();
            await this.setState({ showRelatedBatches: false });
        }
    }

    public override render() {
        return (
            <span>
                <Topbar>
                    <TopbarLeft>
                        <TopbarCrumbActive title={this.state.name} icon="fa-suitcase" />
                    </TopbarLeft>
                    <RoleDependent
                        allowed={[Roles.Administrator, Roles.Client]}
                        content={
                            <TopbarRight>
                                <PanelRouterLink
                                    to={`/batches/${this.props.match.params.id}/edit`}
                                    text={this.props.t('common.editItem', 'Batch')}
                                    icon="fa fa-edit"
                                    small={true}
                                />
                            </TopbarRight>
                        }
                    />
                </Topbar>

                <Content>
                    <LeftTray>
                        <LeftTrayList>
                            <LeftTrayItem title={this.props.t('common.name')}>{this.state.name}</LeftTrayItem>
                            {this.state.inspectionStandardType && (
                                <LeftTrayItem title={this.props.t('common.inspectionType')}>
                                    {this.props.t(`inspectionStandard.${this.state.inspectionStandardType}`)}
                                </LeftTrayItem>
                            )}
                            <LeftTrayItem title={this.props.t('common.status')}>
                                <BatchStatusLabel value={this.state.status} />
                            </LeftTrayItem>
                            {this.state.executorCompanies.map((executorCompany) => (
                                <LeftTrayItem key={executorCompany.company.id} title={this.props.t('common.company')}>
                                    {executorCompany.company.name}
                                </LeftTrayItem>
                            ))}
                            <LeftTrayHeader>{this.props.t('batch.plannedDate')}</LeftTrayHeader>
                            <LeftTrayItem title={this.props.t('common.startDate')}>
                                <NullableFormattedDate value={this.state.plannedStartDate} />
                            </LeftTrayItem>
                            <LeftTrayItem title={this.props.t('common.endDate')}>
                                <NullableFormattedDate value={this.state.plannedEndDate} />
                            </LeftTrayItem>
                            <LeftTrayHeader>{this.props.t('batch.implementationDate')}</LeftTrayHeader>
                            <LeftTrayItem title={this.props.t('common.startDate')}>
                                <NullableFormattedDate value={this.state.startDate} />
                            </LeftTrayItem>
                            <LeftTrayItem title={this.props.t('common.endDate')}>
                                <NullableFormattedDate value={this.state.endDate} />
                            </LeftTrayItem>
                        </LeftTrayList>
                    </LeftTray>

                    <Tray>
                        {this.state.executorsWithSameObject.length > 0 && (
                            <RoleDependent
                                allowed={[Roles.Administrator, Roles.Client]}
                                content={
                                    <Alert severity="info">
                                        <ul>
                                            {this.state.executorsWithSameObject.map((executor) => (
                                                <li key={executor.batch.id + executor.company.id}>
                                                    {this.props.t('batch.multipleExecutorsWarning', executor.company.name, executor.batch.name)}
                                                </li>
                                            ))}
                                        </ul>
                                    </Alert>
                                }
                            />
                        )}

                        <PanelWithoutForm id="objectList">
                            <PanelHeading text={this.props.t('object.list')} icon="fa fa-building">
                                {this.state.batchObjects && this.state.batchObjects.length > 0 && (
                                    <>
                                        <PanelLink
                                            href={`/api/batches/${this.props.match.params.id}/miokExcel?inspectionType=${this.state.inspectionStandardType}`}
                                            text="MJOP Excel export"
                                            isDownload={true}
                                            icon="fa fa-download"
                                            small={true}
                                        />
                                        <PanelLink
                                            href={`/api/batches/${this.props.match.params.id}/batchExcel`}
                                            text="Excel export"
                                            isDownload={true}
                                            icon="fa fa-download"
                                            small={true}
                                        />
                                        <RoleDependent
                                            allowed={[Roles.Administrator, Roles.Client]}
                                            content={
                                                <>
                                                    {this.state.showRelatedBatches ? (
                                                        <PanelButtonLink
                                                            onClick={async () => this.toggleShowRelatedBatches()}
                                                            text={this.props.t('batch.hideRelatedBatches')}
                                                            small
                                                            mt
                                                            icon="fa fa-tasks"
                                                        />
                                                    ) : (
                                                        <PanelButtonLink
                                                            onClick={async () => this.toggleShowRelatedBatches()}
                                                            text={this.props.t('batch.showRelatedBatches')}
                                                            small
                                                            mt
                                                            icon="fa fa-tasks"
                                                        />
                                                    )}
                                                    <PanelRouterLink
                                                        to={`/batches/map?batchId=${this.props.match.params.id}`}
                                                        text={this.props.t('common.map')}
                                                        icon="fa fa-globe"
                                                        small={true}
                                                    />
                                                </>
                                            }
                                        />
                                    </>
                                )}
                            </PanelHeading>
                            {/* Objects List*/}
                            <PanelBody noPadding>
                                <Table hover>
                                    <TableHead>
                                        <TableHeadRow light>
                                            <TableHeader>{this.props.t('common.name')}</TableHeader>
                                            <TableHeader>{this.props.t('common.location')}</TableHeader>
                                            <TableHeader>&nbsp;</TableHeader>
                                        </TableHeadRow>
                                    </TableHead>
                                    {(this.state.isLoading && (
                                        <TableBody>
                                            <TableRow>
                                                <TableData colSpan={3}>
                                                    <Spinner />
                                                </TableData>
                                            </TableRow>
                                        </TableBody>
                                    )) || (
                                        <TableBody>
                                            {this.state.batchObjects.map((item) => (
                                                <React.Fragment key={item.objectId}>
                                                    <TableRow>
                                                        <TableData onClick={async () => this.rowClicked(`/objects/${item.objectId}`)}>
                                                            {item.object.name}
                                                        </TableData>
                                                        <TableData onClick={async () => this.rowClicked(`/objects/${item.objectId}`)}>
                                                            {item.object.location}
                                                        </TableData>
                                                        <TableData alignRight>
                                                            {this.state.status !== BatchStates.Completed && (
                                                                <RoleDependent
                                                                    allowed={[Roles.Administrator, Roles.Client]}
                                                                    content={
                                                                        <IconRemoveButton
                                                                            removeQuestion={this.props.t('common.confirmDelete', item.object.name)}
                                                                            onClick={async () => this.removeBatchObject(item.objectId)}
                                                                        />
                                                                    }
                                                                />
                                                            )}
                                                        </TableData>
                                                    </TableRow>

                                                    {this.state.showRelatedBatches && (
                                                        <>
                                                            <TableRow key={`${item.objectId}rel`}>
                                                                <TableData>&nbsp;</TableData>
                                                                <TableData>
                                                                    <Table>
                                                                        <TableBody>
                                                                            {(item.batches.length > 0 && (
                                                                                <>
                                                                                    {item.batches.map((batch) => (
                                                                                        <TableRow
                                                                                            key={batch.id}
                                                                                            onClick={async () =>
                                                                                                this.rowClicked(`/batches/${batch.id}`)
                                                                                            }
                                                                                        >
                                                                                            <TableData dark>
                                                                                                <NullableFormattedDate
                                                                                                    value={batch.plannedStartDate}
                                                                                                />
                                                                                                &nbsp;-&nbsp;
                                                                                                <NullableFormattedDate value={batch.plannedEndDate} />
                                                                                            </TableData>
                                                                                            <TableData dark>{batch.name}</TableData>
                                                                                            <TableData alignRight dark>
                                                                                                <BatchStatusLabel value={batch.status} />
                                                                                            </TableData>
                                                                                        </TableRow>
                                                                                    ))}
                                                                                </>
                                                                            )) || (
                                                                                <TableRow>
                                                                                    <TableData colSpan={5} dark>
                                                                                        {this.props.t('batch.noRelatedBatches')}{' '}
                                                                                    </TableData>
                                                                                </TableRow>
                                                                            )}
                                                                        </TableBody>
                                                                    </Table>
                                                                </TableData>
                                                                <TableData>&nbsp;</TableData>
                                                            </TableRow>
                                                        </>
                                                    )}
                                                </React.Fragment>
                                            ))}
                                        </TableBody>
                                    )}
                                </Table>
                            </PanelBody>

                            {/* Add Object */}
                            {this.state.status !== BatchStates.Deleted && (
                                <RoleDependent
                                    allowed={[Roles.Administrator, Roles.Client]}
                                    content={
                                        <EditPanelButtons footer>
                                            {this.state.status !== BatchStates.Active && this.state.status !== BatchStates.Completed && (
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        if (window.confirm('Are you sure you would like to remove all objects?')) {
                                                            void this.removeAllBatchObjects(e);
                                                        }
                                                    }}
                                                    className="btn btn-default btn-sm fw600 ml10"
                                                >
                                                    <span className="fa fa-trash pr5"></span> {this.props.t('batch.removeAllObjects')}
                                                </button>
                                            )}
                                            <button
                                                type="button"
                                                onClick={async () => this.setState({ showObjectListForm: true })}
                                                className="btn btn-default btn-sm fw600 ml10"
                                            >
                                                <span className="fa fa-plus pr5"></span> {this.props.t('batch.addMultipleObjects')}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={async () => this.setState({ showObjectForm: true })}
                                                className="btn btn-default btn-sm fw600 ml10"
                                            >
                                                <span className="fa fa-plus pr5"></span>{' '}
                                                {this.props.t('common.addItem', this.props.t('common.object'))}
                                            </button>
                                        </EditPanelButtons>
                                    }
                                />
                            )}
                        </PanelWithoutForm>

                        <PanelWithoutForm id="Documents">
                            <BatchDetailsDocuments batchId={this.state.batchId} />
                        </PanelWithoutForm>
                    </Tray>
                </Content>

                {/* Add object popup */}
                {this.state.showObjectForm && (
                    <BatchDetailsAddObject onAdd={this.addObjectToBatch.bind(this)} onClose={this.handleCloseFormClicked.bind(this)} />
                )}

                {/* Add multiple objects popup */}
                {this.state.showObjectListForm && (
                    <div id="popup_element" className="mw800">
                        <form method="post" action="/" id="admin-form">
                            <div className="panel">
                                <div className="panel-heading">
                                    <span className="panel-title">{this.props.t('batch.addMultipleObjects')}</span>
                                </div>
                                <div className="panel-body">
                                    <div className="section">
                                        <p>{this.props.t('batch.explanationAddMultipleObjects')}</p>
                                        <textarea
                                            style={{ width: '100%' }}
                                            rows={10}
                                            value={this.state.importObjectList}
                                            onChange={async (e) => this.setState({ importObjectList: e.target.value })}
                                        >
                                            {' '}
                                        </textarea>
                                        {this.state.importObjectListResult && (
                                            <div className="mt40 ml20">
                                                <div>
                                                    {this.state.importObjectListResult.succeededCodes.length} {this.props.t('common.added')}
                                                </div>
                                                {this.state.importObjectListResult.notFoundCodes.length > 0 && (
                                                    <div>
                                                        {this.props.t('object.notFoundObjects')}:{' '}
                                                        {this.state.importObjectListResult.notFoundCodes.join(',')}
                                                    </div>
                                                )}
                                                {this.state.importObjectListResult.nonUniqueCodes.length > 0 && (
                                                    <div>
                                                        {this.props.t('object.nonUniqueCodes')}:{' '}
                                                        {this.state.importObjectListResult.nonUniqueCodes.join(',')}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    <div className=" text-right mt10">
                                        <button type="button" onClick={async () => this.handleCloseFormClicked()} className="btn btn-sm fw600 ml10">
                                            {this.props.t('common.close')}
                                        </button>
                                        {this.state.importObjectListResult === undefined && (
                                            <button
                                                type="button"
                                                disabled={this.state.submitting}
                                                onClick={async () => this.handleSaveObjectCollection()}
                                                className="btn btn-primary btn-sm fw600 ml10"
                                            >
                                                {this.props.t('common.addItem', this.props.t('common.objects'))}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>
                )}
                {(this.state.showObjectForm || this.state.showObjectListForm) && (
                    <div id="background_popup" style={{ opacity: 0.6, display: 'block' }} onClick={async () => this.handleCloseFormClicked()}></div>
                )}
            </span>
        );
    }

    private async toggleShowRelatedBatches() {
        await this.setState({ showRelatedBatches: !this.state.showRelatedBatches });
    }

    private async rowClicked(url: string) {
        await this.setState({ showRelatedBatches: false, executorsWithSameObject: [] });
        this.props.navigate(url);
    }

    private async handleCloseFormClicked() {
        await this.setState({ showObjectForm: false, showObjectListForm: false, importObjectListResult: undefined, importObjectList: '' });
    }

    private async addObjectToBatch(objectId: string) {
        const url = `/api/batches/${this.props.match.params.id}/objects`;

        try {
            await this.setState({ submitting: true });
            await fetchPost(url, {}, { objectId });

            await this.loadBatchObjects();
            await this.loadExecutorsWithSameObject();
            await this.setState({
                showObjectForm: false,
            });
        } catch (error) {
            console.error('BatchDetails:handleSaveObject()', error);
        } finally {
            await this.setState({ submitting: false });
        }
    }

    private async handleSaveObjectCollection() {
        await this.setState({ submitting: true });
        if (this.state.importObjectList) {
            const data = { objectList: this.state.importObjectList };
            // TODO: ZXY-1213 remove if bug is fixed
            captureMessage(`BatchDetails:handleSaveObjectCollection(): ${JSON.stringify(data)}`);
            const url = `/api/batches/${this.props.match.params.id}/objects/list`;
            const response = await fetchPost(url, {}, data);
            await this.setState({ importObjectListResult: response.json });
        }
        await this.loadBatchObjects();
        await this.loadExecutorsWithSameObject();
        await this.setState({ submitting: false });
    }

    private async load() {
        await this.setState({ isLoading: true });
        const url = `/api/batches/${this.props.match.params.id}`;
        const result = await fetchGet(url);
        await this.setState(result.json);
        await this.loadBatchObjects();
        await this.setState({ isLoading: false });
    }

    private async loadExecutorsWithSameObject() {
        const url = `/api/batches/${this.props.match.params.id}/executorswithsameobject`;
        const result = await fetchGet(url);
        await this.setState({ executorsWithSameObject: result.json.items });
    }

    private async loadBatchObjects() {
        await this.setState({ submitting: true });
        const url = `/api/batches/${this.props.match.params.id}/objects`;
        const result = await fetchGet(url);
        await this.setState({ batchObjects: result.json });
        await this.setState({ submitting: false });
    }

    private async removeBatchObject(id: string) {
        const url = `/api/batches/${this.props.match.params.id}/object/${id}`;
        await fetchDelete(url);
        await this.loadBatchObjects();
        await this.loadExecutorsWithSameObject();
    }

    private async removeAllBatchObjects(e: any) {
        e.preventDefault();
        await this.setState({ submitting: true });
        const url = `/api/batches/${this.props.match.params.id}/batchObject`;
        await fetchDelete(url, {});
        await this.loadBatchObjects();
        await this.loadExecutorsWithSameObject();
        await this.setState({ submitting: false });
    }
}

// eslint-disable-next-line @typescript-eslint/naming-convention
export const BatchDetails = translate()(BatchDetailsComponent);
